import React, { useEffect, useRef, useState } from 'react';
import { signIn, confirmSignIn, signOut } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

// Phases of the magic-link activation flow.
//  request     – no token in URL: ask for an email and send a fresh link
//  loading     – token present: exchanging it + starting Cognito sign-in
//  setPassword – Cognito accepted the one-time temp password, now pick a real one
//  submitting  – confirming the new password
//  done        – signed in, redirecting into the app
//  linkSent    – a fresh link was just emailed
//  error       – something went wrong; offer to email a fresh link
type Phase =
  | 'request'
  | 'loading'
  | 'setPassword'
  | 'submitting'
  | 'done'
  | 'linkSent'
  | 'noAccount'   // request was for an email with no account (backend 404)
  | 'error';

function passwordProblem(pw: string): string | null {
  if (pw.length < 8) return 'Use at least 8 characters.';
  if (!/[a-z]/.test(pw)) return 'Add a lowercase letter.';
  if (!/[A-Z]/.test(pw)) return 'Add an uppercase letter.';
  if (!/[0-9]/.test(pw)) return 'Add a number.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Add a symbol (like ! or #).';
  return null;
}

export default function Activate() {
  const hasToken = !!new URLSearchParams(window.location.search).get('token');
  const [phase, setPhase] = useState<Phase>(hasToken ? 'loading' : 'request');
  const [purpose, setPurpose] = useState<'activate' | 'reset'>('activate'); // from the signed token
  const [email, setEmail] = useState('');        // locked email for the set-password step
  const [resendEmail, setResendEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const ran = useRef(false);

  // Exchange the token exactly once (guarded against React StrictMode double-invoke —
  // the server burns the token on exchange, so a second call would 409).
  useEffect(() => {
    if (!hasToken || ran.current) return;
    ran.current = true;

    (async () => {
      const token = new URLSearchParams(window.location.search).get('token');
      try {
        // Clear any stale session so signIn with the temp password isn't rejected.
        try { await signOut(); } catch { /* nothing to sign out of */ }

        const res = await fetch(`${API_URL}/auth/magic-link/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (!res.ok) {
          setError(
            res.status === 409
              ? 'This link has already been used — links only work once. Get a fresh one below.'
              : 'This link has expired or is no longer valid. Get a fresh one below.'
          );
          setPhase('error');
          return;
        }

        const data = await res.json();
        setEmail(data.email || '');
        setResendEmail(data.email || '');
        setPurpose(data.purpose === 'reset' ? 'reset' : 'activate');

        const out = await signIn({ username: data.email, password: data.tempPassword });
        if (out.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setPhase('setPassword');
        } else if (out.isSignedIn) {
          window.location.href = '/';
        } else {
          setError('We couldn’t start your sign-in. Get a fresh link below.');
          setPhase('error');
        }
      } catch {
        setError('Something went wrong opening your link. Get a fresh one below.');
        setPhase('error');
      }
    })();
  }, [hasToken]);

  async function handleSetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const problem = passwordProblem(password);
    if (problem) { setError(problem); return; }
    if (password !== confirm) { setError('Those two passwords don’t match.'); return; }

    setPhase('submitting');
    try {
      const out = await confirmSignIn({ challengeResponse: password });
      if (out.isSignedIn) {
        setPhase('done');
        window.setTimeout(() => { window.location.href = '/'; }, 1200);
      } else {
        setError('Almost there — please try setting your password once more.');
        setPhase('setPassword');
      }
    } catch (err: any) {
      setError(err?.message || 'That password wasn’t accepted. Try a different one.');
      setPhase('setPassword');
    }
  }

  async function handleRequestLink(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    // Lowercase: Cognito usernames are the email and case-sensitive; the backend also
    // lowercases + resolves the canonical username, but normalize here too.
    const addr = (resendEmail || '').trim().toLowerCase();  // case-insensitive: normalize
    if (!addr || !addr.includes('@')) { setError('Please enter the email address you use to sign in.'); return; }
    try {
      const res = await fetch(`${API_URL}/auth/magic-link/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addr }),
      });
      if (res.status === 404) {          // enumeration-safe: always show "Check your email"
        setPhase('linkSent');
        return;
      }
      if (!res.ok) {
        setError('Couldn’t send the link just now. Please try again in a moment.');
        return;
      }
      setPhase('linkSent');
    } catch {
      setError('Couldn’t send the link just now. Please try again in a moment.');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f0f9ff] to-[#e5e7eb] px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <img src="/nfe-logo.png" alt="No Flipping Excuses" className="h-12 mx-auto mb-6" />

        {phase === 'loading' && (
          <div className="text-center py-6">
            <div className="mx-auto mb-4 h-10 w-10 rounded-full border-4 border-tdb-accent border-t-transparent animate-spin" />
            <p className="text-[#32373c] font-semibold">Opening your secure link…</p>
            <p className="text-sm text-gray-500 mt-1">One moment while we sign you in.</p>
          </div>
        )}

        {phase === 'setPassword' && (
          <form onSubmit={handleSetPassword}>
            <h1 className="text-2xl font-bold text-[#32373c] text-center">
              {purpose === 'reset' ? 'Reset your password' : 'Create your password'}
            </h1>
            <p className="text-sm text-gray-500 text-center mt-1 mb-5">
              {purpose === 'reset' ? (
                <>Signed in as <span className="font-semibold text-[#32373c]">{email}</span>. Choose a new password below.</>
              ) : (
                <>You’re signing in as <span className="font-semibold text-[#32373c]">{email}</span>. Pick a password and you’re in.</>
              )}
            </p>

            <label className="block text-sm font-semibold text-[#32373c] mb-1">New password</label>
            <input
              type="password"
              autoFocus
              data-testid="activate-new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 mb-3 focus:outline-none focus:ring-2 focus:ring-tdb-accent"
              placeholder="At least 8 characters"
            />
            <label className="block text-sm font-semibold text-[#32373c] mb-1">Confirm password</label>
            <input
              type="password"
              data-testid="activate-confirm-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 mb-2 focus:outline-none focus:ring-2 focus:ring-tdb-accent"
              placeholder="Type it again"
            />
            <p className="text-xs text-gray-400 mb-4">
              Needs an uppercase &amp; lowercase letter, a number, and a symbol.
            </p>

            {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">{error}</p>}

            <button
              type="submit"
              data-testid="activate-submit"
              className="w-full bg-tdb-accent text-white py-3 rounded-xl font-bold hover:bg-[#0ea5ef] transition-all shadow-lg hover:shadow-xl"
            >
              Set password &amp; sign in
            </button>
          </form>
        )}

        {phase === 'submitting' && (
          <div className="text-center py-6">
            <div className="mx-auto mb-4 h-10 w-10 rounded-full border-4 border-tdb-accent border-t-transparent animate-spin" />
            <p className="text-[#32373c] font-semibold">Setting your password…</p>
          </div>
        )}

        {phase === 'done' && (
          <div className="text-center py-6">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <span className="text-green-600 text-2xl">✓</span>
            </div>
            <p className="text-[#32373c] font-bold text-lg">You’re all set!</p>
            <p className="text-sm text-gray-500 mt-1">Taking you into the portal…</p>
          </div>
        )}

        {(phase === 'request' || phase === 'error') && (
          <form onSubmit={handleRequestLink}>
            <h1 className="text-2xl font-bold text-[#32373c] text-center">
              {phase === 'error' ? 'Let’s get you a fresh link' : 'Email me a sign-in link'}
            </h1>
            <p className="text-sm text-gray-500 text-center mt-1 mb-5">
              Enter your email and we’ll send a secure link that signs you in — no password to remember.
            </p>

            {phase === 'error' && error && (
              <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3">{error}</p>
            )}

            <label className="block text-sm font-semibold text-[#32373c] mb-1">Email</label>
            <input
              type="email"
              autoFocus
              data-testid="activate-email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 mb-2 focus:outline-none focus:ring-2 focus:ring-tdb-accent"
              placeholder="you@example.com"
            />

            {phase === 'request' && error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">{error}</p>
            )}

            <button
              type="submit"
              data-testid="activate-send-link"
              className="w-full bg-tdb-accent text-white py-3 rounded-xl font-bold hover:bg-[#0ea5ef] transition-all shadow-lg hover:shadow-xl mt-2"
            >
              Send my sign-in link
            </button>
            <a href="/" className="block text-center text-sm text-gray-500 mt-4 hover:text-[#32373c]">
              Back to sign in
            </a>
          </form>
        )}

        {phase === 'linkSent' && (
          <div className="text-center py-4">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-[#f0f9ff] flex items-center justify-center">
              <span className="text-tdb-accent text-2xl">✉</span>
            </div>
            <h1 className="text-2xl font-bold text-[#32373c]">Check your email</h1>
            <p className="text-sm text-gray-500 mt-2">
              A secure sign-in link is on its way to <span className="font-semibold text-[#32373c]">{resendEmail}</span>.
              It works once and expires in 7 days.
            </p>
            <a href="/" className="inline-block text-sm text-tdb-accent font-semibold mt-5 hover:underline">
              Back to sign in
            </a>
          </div>
        )}

        {phase === 'noAccount' && (
          <div className="text-center py-4">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-[#fef2f2] flex items-center justify-center">
              <span className="text-red-500 text-2xl">⚠</span>
            </div>
            <h1 className="text-2xl font-bold text-[#32373c]">No account found</h1>
            <p className="text-sm text-gray-500 mt-2">
              We couldn’t find a JV Portal account for <span className="font-semibold text-[#32373c]">{resendEmail}</span>.
              To request access, please contact{' '}
              <a href="mailto:help@jasonlucchesi.com" className="text-tdb-accent font-semibold hover:underline">
                help@jasonlucchesi.com
              </a>.
            </p>
            <a href="/" className="inline-block text-sm text-tdb-accent font-semibold mt-5 hover:underline">
              Back to sign in
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
